import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// Side-effect import: patches L.Map with rotation support (the `rotate`
// option below). Two-finger gesture on touch, Shift+drag on desktop.
import 'leaflet-rotate';
import { config } from '../config';
import {
  LOTS,
  computeAreaM2,
  findLotById,
  lotMetaStore,
  lotStatus,
  selectedLotStore,
  type LotSpec,
  type LotStatus,
} from '../data/plots';

// Hover tooltip text: "ID (area)" — e.g. "B05 (10,546 m²)". Prefers the
// published Sheet area, falling back to polygon math before the meta loads.
function lotTooltip(lot: LotSpec): string {
  const area = lotMetaStore.getById(lot.id)?.area ?? computeAreaM2(lot.corners);
  return `${lot.id} (${Math.round(area).toLocaleString('en-US')} m²)`;
}
import { REF_POINTS, perimeterOutline, PERIMETER_OUTSET_M } from '../data/refPoints';
import { ROADS, type RoadEnd } from '../data/roads';
import { warmEsriTileCache } from './warmTileCache';

/**
 * Lightweight 2D fallback viewer used in low-data mode. Loads ~40 KB of
 * Leaflet + OSM tiles instead of Cesium's ~6 MB Photoreal pipeline. Same
 * `selectedLotStore` wiring as CesiumViewer so the offer form and the rest
 * of the UI keep working.
 */

// Esri World Imagery — free, no API key needed. Native detail in rural areas
// like Manglillo caps out around zoom 18.
const SATELLITE_TILE_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const SATELLITE_ATTRIBUTION =
  'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community';

const STATUS_COLOR: Record<LotStatus, string> = {
  available: '#22c55e',         // green-500
  'under-contract': '#f97316',  // orange-500
  pending: '#eab308',           // yellow-500
  sold: '#ef4444',              // red-500
};

const STATUS_FILL_OPACITY: Record<LotStatus, number> = {
  available: 0.3,
  'under-contract': 0.3,
  pending: 0.3,
  sold: 0.25,
};

const SELECTED_FILL = '#facc15';
const SELECTED_FILL_OPACITY = 0.7;
const SELECTED_BORDER = '#facc15';
const DEFAULT_BORDER_OPACITY = 0.45;
const DEFAULT_BORDER_WEIGHT = 1.5;
const SELECTED_BORDER_WEIGHT = 4;

function styleForLot(lot: LotSpec, selected: boolean): L.PathOptions {
  if (selected) {
    return {
      color: SELECTED_BORDER,
      weight: SELECTED_BORDER_WEIGHT,
      opacity: 1,
      fillColor: SELECTED_FILL,
      fillOpacity: SELECTED_FILL_OPACITY,
    };
  }
  const status = lotStatus(lot);
  return {
    color: STATUS_COLOR[status],
    weight: DEFAULT_BORDER_WEIGHT,
    opacity: DEFAULT_BORDER_OPACITY,
    fillColor: STATUS_COLOR[status],
    fillOpacity: STATUS_FILL_OPACITY[status],
  };
}

export function LeafletViewer() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const sv = config.lowDataResetView;
    // Honour shared-view URLs: ?lon=&lat=&z=&b= takes precedence over the
    // pinned start view so a link copied from "Share this view" lands the
    // visitor on the same map state.
    const params = new URLSearchParams(window.location.search);
    const sharedLon = parseFloat(params.get('lon') ?? '');
    const sharedLat = parseFloat(params.get('lat') ?? '');
    const sharedZoom = parseFloat(params.get('z') ?? '');
    const sharedBearing = parseFloat(params.get('b') ?? '');
    const initLat = Number.isFinite(sharedLat) ? sharedLat : sv.latitude;
    const initLon = Number.isFinite(sharedLon) ? sharedLon : sv.longitude;
    const initZoom = Number.isFinite(sharedZoom) ? sharedZoom : sv.zoom;
    const initBearing = Number.isFinite(sharedBearing) ? sharedBearing : sv.bearing;
    const map = L.map(containerRef.current, {
      attributionControl: true,
      zoomControl: false,
      // SVG (not canvas) for vector overlays. With canvas + leaflet-rotate,
      // pinch+rotate gestures applied a composite CSS transform to the
      // canvas bitmap which made lot polygons visibly skew relative to
      // their lat/lon until touchend, then snap back. SVG paths are DOM
      // elements whose `d` attribute is recomputed by Leaflet on every
      // reprojection, and the rotation transform applies to the parent
      // `<g>` element so each path rotates with the tiles. Performance
      // is fine for our handful of polygons.
      preferCanvas: false,
      // Esri's native detail for Manglillo runs out before its nominal
      // max — cap at 17 so the user never sees the "Map data not yet
      // available" placeholder.
      maxZoom: 17,
      // leaflet-rotate plugin: enables map rotation. Touch rotation is
      // off because leaflet-rotate has a pivot-mismatch bug during
      // pinch+rotate that makes overlay polygons drift relative to the
      // tiles during the gesture. Desktop keeps Shift+drag, and the
      // "Reset" / preset menu buttons can still program the bearing.
      rotate: true,
      bearing: 0,
      touchRotate: false,
      shiftKeyRotate: true,
      rotateControl: false,
    } as L.MapOptions).setView([initLat, initLon], initZoom);
    // setBearing is added by leaflet-rotate. Apply the initial bearing — from
    // the shared URL when provided, otherwise from `lowDataResetView` so the
    // first paint already matches the 🔄 Reset / 🏝️ El Manglillo preset.
    const m = map as L.Map & { setBearing?: (deg: number) => void };
    m.setBearing?.(initBearing);

    // Expose the map for in-page debugging: from devtools you can call
    // `leafletMap.getCenter()` / `leafletMap.getZoom()` to capture a new
    // default view and paste it into config.lowDataResetView.
    (window as unknown as { leafletMap: L.Map }).leafletMap = map;


    // Satellite tiles ride below the polygon overlay pane (z-index 200 vs
    // overlayPane's 400) so the lots stay visible on the dark map background
    // while tiles are still streaming in over a slow connection.
    L.tileLayer(SATELLITE_TILE_URL, {
      maxZoom: 17,
      attribution: SATELLITE_ATTRIBUTION,
    }).addTo(map);

    // First online visit: warm the SW cache with every Esri tile that
    // covers the site bbox (zooms 14–17). Next time the user opens the
    // PWA offline — even before panning around — the satellite imagery
    // is already there. Runs in idle time, fully silent on failure.
    warmEsriTileCache();

    // Draw order matters with the canvas renderer: layers added later
    // render above layers added earlier. We want:
    //   1. Roads        → bottom
    //   2. Perimeter polyline (yellow)
    //   3. P markers (cyan)
    //   4. Lot polygons → top
    const perimeterIndices = config.perimeterRefPoints;
    const refByNumber = new Map<number, (typeof REF_POINTS)[number]>();
    for (const p of REF_POINTS) {
      const n = parseInt(p.label.replace(/^P/, ''), 10);
      if (Number.isFinite(n)) refByNumber.set(n, p);
    }

    // Roads first — same width/side-offset math as Cesium's roadsLayer.
    const refById = new Map(REF_POINTS.map((p) => [p.id, p] as const));
    const resolveEnd = (end: RoadEnd) =>
      'refId' in end
        ? refById.get(end.refId)
          ? { lon: refById.get(end.refId)!.lon, lat: refById.get(end.refId)!.lat }
          : null
        : { lon: end.lon, lat: end.lat };
    for (const road of ROADS) {
      const from = resolveEnd(road.from);
      const to = resolveEnd(road.to);
      if (!from || !to) continue;
      const midLat = (from.lat + to.lat) / 2;
      const cosLat = Math.cos((midLat * Math.PI) / 180);
      const M_PER_DEG_LON = 111320 * cosLat;
      const M_PER_DEG_LAT = 110574;
      const dxM = (to.lon - from.lon) * M_PER_DEG_LON;
      const dyM = (to.lat - from.lat) * M_PER_DEG_LAT;
      const len = Math.hypot(dxM, dyM);
      if (len === 0) continue;
      const ux = dxM / len;
      const uy = dyM / len;
      const rx = uy;
      const ry = -ux;
      let leftM = 0;
      let rightM = 0;
      if (road.side === 'right') rightM = road.widthM;
      else if (road.side === 'left') leftM = road.widthM;
      else {
        leftM = road.widthM / 2;
        rightM = road.widthM / 2;
      }
      const lOff = {
        dLon: (-rx * leftM) / M_PER_DEG_LON,
        dLat: (-ry * leftM) / M_PER_DEG_LAT,
      };
      const rOff = {
        dLon: (rx * rightM) / M_PER_DEG_LON,
        dLat: (ry * rightM) / M_PER_DEG_LAT,
      };
      const corners: L.LatLngTuple[] = [
        [from.lat + lOff.dLat, from.lon + lOff.dLon],
        [to.lat + lOff.dLat, to.lon + lOff.dLon],
        [to.lat + rOff.dLat, to.lon + rOff.dLon],
        [from.lat + rOff.dLat, from.lon + rOff.dLon],
      ];
      L.polygon(corners, {
        color: '#C8B89E',
        weight: 1,
        opacity: 0.7,
        fillColor: '#E4D4BB',
        fillOpacity: 0.9,
      }).addTo(map);
    }

    // Perimeter polyline (yellow) — drawn after roads so it sits on top.
    // Pushed slightly outward from the true border (the P-dots stay on the
    // true corners) so the outline doesn't overlap the interior streets/lots.
    const perimeterLatLngs: L.LatLngTuple[] = perimeterOutline(
      perimeterIndices,
      PERIMETER_OUTSET_M,
    ).map((c) => [c.lat, c.lon]);
    if (perimeterLatLngs.length > 0) {
      perimeterLatLngs.push(perimeterLatLngs[0]);
      L.polyline(perimeterLatLngs, {
        color: '#fbbf24', // amber-400 — matches Cesium perimeter colour
        weight: 3,
        opacity: 0.9,
      }).addTo(map);
    }

    // Perimeter ref-point markers (P1, P2, P11, P12, P13).
    for (const n of perimeterIndices) {
      const p = refByNumber.get(n);
      if (!p) continue;
      const marker = L.circleMarker([p.lat, p.lon], {
        radius: 6,
        color: '#fff',
        weight: 2,
        fillColor: '#06b6d4', // cyan-500
        fillOpacity: 1,
      }).addTo(map);
      marker.bindTooltip(p.label, {
        permanent: true,
        direction: 'top',
        offset: [0, -8],
        className: 'manglillo-ref-label',
      });
    }

    // Draw each lot as a Leaflet polygon. We keep references so the
    // store-subscription below can flip styling on selection without
    // recreating layers.
    const lotLayers = new Map<string, L.Polygon>();
    for (const lot of LOTS) {
      // Draw the cosmetic displayCorners when present; measurements still use corners.
      const latlngs: L.LatLngTuple[] = (lot.displayCorners ?? lot.corners).map((c) => [c.lat, c.lon]);
      const isSelected = selectedLotStore.get()?.id === lot.id;
      const poly = L.polygon(latlngs, styleForLot(lot, isSelected));
      poly.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        selectedLotStore.set(lot);
      });
      poly.bindTooltip(lotTooltip(lot), { sticky: true });
      poly.addTo(map);
      lotLayers.set(lot.id, poly);
    }

    // Deselect when the user taps empty map area.
    map.on('click', () => selectedLotStore.set(null));

    // Restyle on selection or CSV-status changes.
    const restyleAll = () => {
      const sel = selectedLotStore.get();
      lotLayers.forEach((layer, id) => {
        const lot = findLotById(id);
        if (!lot) return;
        layer.setStyle(styleForLot(lot, sel?.id === id));
        // Refresh the "ID (area)" tooltip once the Sheet area loads.
        layer.setTooltipContent(lotTooltip(lot));
        if (sel?.id === id) layer.bringToFront();
      });
    };
    const unsubSelection = selectedLotStore.subscribe(restyleAll);
    const unsubMeta = lotMetaStore.subscribe(restyleAll);

    // URL bar sync — match CesiumViewer behaviour: ?lot=<id> only, no
    // camera params (those live in the "Share this view" snapshot).
    let lastUrl = '';
    const writeUrl = () => {
      const u = new URL(window.location.href);
      u.searchParams.delete('lon');
      u.searchParams.delete('lat');
      u.searchParams.delete('h');
      u.searchParams.delete('heading');
      u.searchParams.delete('pitch');
      const lot = selectedLotStore.get();
      if (lot) u.searchParams.set('lot', lot.id);
      else u.searchParams.delete('lot');
      const next = u.toString();
      if (next !== lastUrl) {
        lastUrl = next;
        window.history.replaceState(null, '', next);
      }
    };
    const unsubUrl = selectedLotStore.subscribe(writeUrl);

    // Honor ?lot=<id> on initial load: select. When a shared lon/lat is
    // also present we keep that camera; otherwise we frame the lot.
    const lotId = params.get('lot');
    if (lotId) {
      const lot = findLotById(lotId);
      if (lot) {
        selectedLotStore.set(lot);
        const hasSharedView = Number.isFinite(sharedLat) && Number.isFinite(sharedLon);
        if (!hasSharedView) {
          const layer = lotLayers.get(lot.id);
          if (layer) map.fitBounds(layer.getBounds(), { padding: [40, 40] });
        }
      }
    }

    // Tell the boot screen to dismiss now that the map is live.
    window.dispatchEvent(new CustomEvent('manglillo:viewer-ready'));

    return () => {
      unsubSelection();
      unsubMeta();
      unsubUrl();
      map.remove();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        inset: 0,
        background: '#0a0a0a',
        // Pin a stacking context here so Leaflet's internal panes (which use
        // z-index up to ~700 for tooltips) stay confined below our overlay
        // UI (LotDetails, footer, banner — all z-index ≥ 5).
        zIndex: 0,
      }}
    />
  );
}
